/**
 * Project workspace container.
 *
 * Owns the state every screen shares — project, well list, active well — so
 * switching between the log, crossplot, correlation, map and analysis screens
 * keeps the same well selected without refetching it each time.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, unpage, type Page } from "../api";
import { canWrite } from "../auth";
import { CurveOpsDialog } from "../components/CurveOpsDialog";
import { DataManager, type DataTab } from "../components/DataManager";
import { Icon } from "../components/icons";
import { LogColorEditor } from "../components/LogColorEditor";
import { PaletteEditorHost, useProjectPalettes } from "../components/PaletteEditor";
import type { UserRamp } from "../lib/userramps";
import {
  Button,
  Dropdown,
  Empty,
  Loading,
  Modal,
  Notice,
  NumberField,
  Panel,
  TextField,
  useToast,
} from "../components/ui";
import { useAsync, useStored } from "../hooks";
import { pickDefaultWell } from "../lib/wells";
import { AppShell, type ScreenKey } from "../shell/AppShell";
import type { AuditEvent, LasUpload, Project, Well } from "../types";
import { APP_VERSION } from "../version";
import Analysis from "./Analysis";
import Dashboard from "./Dashboard";
import GeoModelScreen from "./GeoModel";
import Lrlc from "./Lrlc";
import LogEstimate from "./LogEstimate";
import CoreScreen from "./CoreScreen";
import Correlation from "./Correlation";
import Crossplot from "./Crossplot";
import FieldMap from "./FieldMap";
import Histogram from "./Histogram";
import ImageLogScreen from "./ImageLogScreen";
import Workstation from "./Workstation";

export default function Workspace({ screen }: { screen: ScreenKey }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const id = Number(projectId);

  const [lastWell, setLastWell] = useStored<Record<string, number>>("evolog.lastWell", {});
  const [activeWellId, setActiveWellId] = useState<number | null>(null);
  const [dialog, setDialog] = useState<string | null>(null);
  /**
   * Bumped whenever the project's data changes underneath the screens.
   *
   * Curve edits are launched from the menu bar, which lives here rather than
   * inside the screen showing the curve, so the screen has no way to know the
   * samples it drew a second ago have been superseded. Reloading the well list
   * is not enough — a screen that fetches its curves keyed on `well.id` sees no
   * change in the key and keeps its stale copy, which is the worst outcome
   * available: a despiked curve still showing its spike.
   */
  const [dataVersion, setDataVersion] = useState(0);

  const project = useAsync(
    (signal) => api.get<Project>(`/api/projects/${id}/`, undefined, signal),
    [id],
  );
  const wells = useAsync(
    (signal) => api.get<Page<Well>>("/api/wells/", { project: id }, signal).then((page) => unpage(page, signal)),
    [id],
  );

  // Palet rumah milik proyek, terpasang di setiap layar dan tertulis kembali
  // ke proyeknya dari pita warna mana pun — bukan hanya dari lembar korelasi.
  const mayWritePalettes = project.data ? canWrite(project.data.my_role) : false;
  const tulisPalet = useCallback(
    (list: UserRamp[]) => {
      void api
        .put(`/api/projects/${id}/palettes/`, { palettes: list })
        .catch((cause) => toast(cause instanceof Error ? cause.message : String(cause), "error"));
    },
    [id, toast],
  );
  useProjectPalettes(
    project.data?.id,
    project.data?.palettes,
    mayWritePalettes ? tulisPalet : undefined,
  );

  // Prefer the last well used in this project, then the first the sheet can draw.
  //
  // Dijalankan juga ketika sumur yang aktif tidak ada lagi di daftar — sebuah
  // sumur yang baru saja dihapus dari pohon data. Tanpa itu, `activeWellId`
  // menunjuk sumur yang tidak ada, layarnya berbunyi "proyek ini belum punya
  // sumur" pada proyek berisi lima, dan tidak ada satu pun jalan kembali
  // selain memuat ulang halamannya.
  useEffect(() => {
    if (!wells.data?.length) return;
    if (activeWellId !== null && wells.data.some((well) => well.id === activeWellId)) return;
    const found = pickDefaultWell(wells.data, lastWell[String(id)]);
    if (found) setActiveWellId(found.id);
  }, [wells.data, activeWellId, id, lastWell]);

  const activeWell = useMemo(
    () => wells.data?.find((well) => well.id === activeWellId) ?? null,
    [wells.data, activeWellId],
  );

  const selectWell = (wellId: number) => {
    setActiveWellId(wellId);
    setLastWell({ ...lastWell, [String(id)]: wellId });
  };

  const refresh = () => {
    wells.reload();
    project.reload();
    setDataVersion((version) => version + 1);
  };

  const handleAction = (action: string) => {
    if (action === "print-cpi") {
      if (!activeWell) toast("Select a well first.", "error");
      else if (activeWell.status === "planned")
        toast(`${activeWell.name} is a planned well — not drilled yet.`, "error");
      else window.open(`/print/cpi/${activeWell.id}`, "_blank");
      return;
    }
    if (action === "print-cpi-multi") {
      window.open(`/projects/${id}/print/cpi`, "_blank");
      return;
    }
    if (action === "export-map") {
      navigate(`/projects/${id}/map`);
      return;
    }
    // Butir menu citra membuka layarnya; panelnya sendiri ada di rel kanan
    // layar itu. Membuka dialog terpisah untuk pekerjaan yang menuntut citranya
    // terlihat berarti menutupi satu-satunya hal yang sedang dibaca.
    if (action.startsWith("image:")) {
      if (!activeWell) toast("Pilih sumur lebih dulu.", "error");
      else navigate(`/projects/${id}/image`);
      return;
    }
    if (action === "import-image" || action === "import-plugs") {
      if (!activeWell) {
        toast("Pilih sumur lebih dulu.", "error");
        return;
      }
      if (action === "import-plugs") navigate(`/projects/${id}/core`);
      else setDialog(action);
      return;
    }
    // Ketiga butir menu LRLC membuka layar yang sama pada keadaan yang
    // berbeda, dan keadaan itu dititipkan pada query string dan bukan pada
    // keadaan React yang dioper ke bawah. Alasannya bisa ditempel: sebuah
    // lembar log yang terbuka pada sumur tertentu adalah hal yang orang kirim
    // ke sebelahnya, dan tautan yang memulihkannya hanya ada kalau letaknya
    // tertulis di alamatnya. Ia juga selamat dari muat ulang halaman, yang
    // tidak dipunyai satu pun keadaan yang hanya hidup di memori.
    if (action.startsWith("lrlc:")) {
      const what = action.slice("lrlc:".length);
      if (what === "sheet" && !activeWell) {
        toast("Pilih sumur lebih dulu.", "error");
        return;
      }
      const query = what === "sheet" ? `?sheet=${activeWell!.id}`
        : what === "tests" ? "?tab=tests"
        : "";
      navigate(`/projects/${id}/lrlc${query}`);
      return;
    }
    if (action.startsWith("multimin:") || action === "run-deterministic" || action === "batch") {
      navigate(`/projects/${id}/analysis`);
      return;
    }
    if (action.startsWith("curve-op:")) {
      // Every curve edit reads and rewrites one well's curves, so there is
      // nothing sensible to open without one chosen.
      if (!activeWell) toast("Pick a well first.", "error");
      else setDialog(action);
      return;
    }
    setDialog(action);
  };

  // Hanya pemuatan *pertama* yang mengosongkan layar. Tiap tulisan dari
  // pengelola data memanggil `refresh()`, dan mengembalikan <Loading> di sini
  // pada pemuatan ulang berarti seluruh pohon dilepas lalu dipasang kembali:
  // jendela Data terbangun dari awal, dan zona yang sedang dipilih, penyaring
  // yang sedang dipasang serta kotak centang yang sedang terisi hilang setiap
  // kali sesuatu tersimpan. Data lama tetap tergambar sampai yang baru tiba —
  // itulah yang sudah dilakukan `useAsync`, yang tidak pernah mengosongkan
  // `data` selama memuat ulang.
  if ((project.loading && !project.data) || (wells.loading && !wells.data))
    return <Loading label="Loading project…" />;
  if (project.error)
    return (
      <div style={{ padding: 24 }}>
        <Notice tone="error">{project.error}</Notice>
        <div style={{ marginTop: 12 }}>
          <Button onClick={() => navigate("/projects")}>Back to the project list</Button>
        </div>
      </div>
    );
  if (!project.data) return null;

  const body = () => {
    if (!activeWell) {
      return (
        <Empty>
          This project has no wells yet.
          {canWrite(project.data!.my_role) && (
            <Button variant="primary" onClick={() => setDialog("import-las")}>
              {Icon.upload({ size: 13 })} Import a LAS file
            </Button>
          )}
        </Empty>
      );
    }
    switch (screen) {
      case "dashboard":
        return <Dashboard project={project.data!} />;
      case "log":
        return (
          <Workstation
            project={project.data!}
            well={activeWell}
            wells={wells.data ?? []}
            dataVersion={dataVersion}
            onDataChanged={refresh}
          />
        );
      case "crossplot":
        return (
          <Crossplot
            project={project.data!}
            well={activeWell}
            wells={wells.data ?? []}
            dataVersion={dataVersion}
            onDataChanged={refresh}
            onWellChange={selectWell}
          />
        );
      case "histogram":
        return (
          <Histogram
            project={project.data!}
            well={activeWell}
            wells={wells.data ?? []}
            dataVersion={dataVersion}
            onWellChange={selectWell}
          />
        );
      case "correlation":
        return <Correlation project={project.data!} well={activeWell} dataVersion={dataVersion} />;
      case "map":
        return <FieldMap project={project.data!} well={activeWell} onWellChange={selectWell} />;
      case "estimate":
        return (
          <LogEstimate
            project={project.data!}
            well={activeWell}
            wells={wells.data ?? []}
            onDataChanged={refresh}
            onWellChange={selectWell}
          />
        );
      case "lrlc":
        return (
          <Lrlc
            project={project.data!}
            well={activeWell}
            wells={wells.data ?? []}
            onWellChange={selectWell}
          />
        );
      case "geomodel":
        return <GeoModelScreen project={project.data!} />;
      case "image":
        return (
          <ImageLogScreen
            project={project.data!}
            well={activeWell}
            onDataChanged={refresh}
          />
        );
      case "core":
        return (
          <CoreScreen
            project={project.data!}
            well={activeWell}
            onDataChanged={refresh}
          />
        );
      case "analysis":
        return (
          <Analysis
            project={project.data!}
            well={activeWell}
            wells={wells.data ?? []}
            onDataChanged={refresh}
          />
        );
    }
  };

  return (
    <>
      <PaletteEditorHost />
      <AppShell
        project={project.data}
        wells={wells.data ?? []}
        activeWell={activeWell}
        onWellChange={selectWell}
        screen={screen}
        onAction={handleAction}
        toolbar={
          <>
            <Button variant="ghost" onClick={() => navigate("/projects")} title="All projects" style={{ height: 26 }}>
              {Icon.back({ size: 12 })} Projects
            </Button>
            {canWrite(project.data.my_role) && (
              <Button variant="ghost" onClick={() => setDialog("import-las")} style={{ height: 26 }}>
                {Icon.upload({ size: 12 })} Import LAS
              </Button>
            )}
            {activeWell && (
              <Button
                variant="ghost"
                disabled={activeWell.status === "planned"}
                title={
                  activeWell.status === "planned"
                    ? `${activeWell.name} is a planned well — not drilled yet`
                    : undefined
                }
                onClick={() => window.open(`/print/cpi/${activeWell.id}`, "_blank")}
                style={{ height: 26 }}
              >
                {Icon.print({ size: 12 })} CPI A3
              </Button>
            )}
          </>
        }
        status={
          activeWell ? (
            <>
              <span>{activeWell.name}</span>
              <span style={{ width: 1, height: 11, background: "var(--line)" }} />
              <span>
                {activeWell.zones.length} zones · {activeWell.markers.length} markers
              </span>
            </>
          ) : undefined
        }
      >
        {body()}
      </AppShell>

      {dialog === "import-las" && (
        <ImportLasDialog
          project={project.data}
          onClose={() => setDialog(null)}
          onImported={(upload) => {
            toast(upload.message);
            setDialog(null);
            refresh();
          }}
        />
      )}
      {dialog === "activity" && <ActivityDialog project={project.data} onClose={() => setDialog(null)} />}
      {/* Warna lembar milik proyek, jadi ia dibuka dari sini dan bukan dari
          salah satu lembar: kolom RES lembar LRLC, kelas di crossplot yang
          duduk di sebelahnya, dan batupasir di lembar cetak CPI adalah satu
          keputusan yang sama. */}
      {dialog === "log-colors" && (
        <LogColorEditor
          projectId={project.data.id}
          mayWrite={canWrite(project.data.my_role)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "import-image" && activeWell && (
        <ImportImageDialog
          well={activeWell}
          onClose={() => setDialog(null)}
          onImported={(name) => {
            toast(`${name} termuat.`);
            setDialog(null);
            navigate(`/projects/${id}/image`);
          }}
        />
      )}
      {dialog?.startsWith("data:") && (
        <DataManager
          project={project.data}
          wells={wells.data ?? []}
          activeWellId={activeWellId}
          tab={dialog.slice("data:".length) as DataTab}
          onTab={(tab) => setDialog(`data:${tab}`)}
          onWellSelect={selectWell}
          onOpenProject={(other) => {
            setDialog(null);
            // Layar yang sama pada lapangan yang lain: orang yang berpindah
            // lapangan dari lembar korelasi sedang mengerjakan korelasi, dan
            // melemparnya ke dasbor berarti dua klik untuk kembali ke tempat
            // ia tadi berdiri.
            if (other !== id) navigate(`/projects/${other}/${screen}`);
          }}
          onChanged={refresh}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.startsWith("curve-op:") && activeWell && (
        <CurveOpsDialog
          project={project.data}
          well={activeWell}
          operation={dialog.slice("curve-op:".length)}
          onClose={() => setDialog(null)}
          onApplied={refresh}
        />
      )}
      {dialog === "about" && (
        <Modal title="About EvoLoG" onClose={() => setDialog(null)}>
          {/* Versinya disebut lebih dulu: yang dicari orang ketika membuka
              dialog ini adalah nomor yang akan ditulisnya di dalam laporan
              cacat, bukan penjelasan tentang apa aplikasi ini. */}
          <span style={{ font: "500 12px var(--font-mono)", color: "var(--ink)" }}>
            EvoLoG {APP_VERSION}
          </span>
          <p style={{ margin: 0, font: "400 12px/1.6 var(--font-sans)", color: "var(--ink-2)" }}>
            A multi-user web application for formation evaluation and petrophysics. Django runs the
            computation engine — shale volume, porosity, water saturation, permeability, cutoffs and the
            multi-mineral solver — and stores every interpretation as a version that never changes again,
            together with the parameter set that produced it; this React interface is what shows it.
          </p>
          <Panel title="This project">
            <span style={{ font: "400 11px var(--font-mono)", color: "var(--ink-3)" }}>
              {project.data.name} · {project.data.crs} · {project.data.well_count} wells ·{" "}
              {project.data.curve_count} curves
            </span>
          </Panel>
        </Modal>
      )}
      {dialog === "shortcuts" && (
        /* Yang tercantum di sini harus bisa dicoba sambil membacanya. Daftar
           ini pernah menjanjikan Ctrl+1, Ctrl+2 dan Ctrl+O yang tidak pernah
           dipasang di mana pun — dan sebuah pintasan yang diiklankan lalu
           tidak bekerja membuat orang meragukan tombol yang sungguh ada. */
        <Modal title="Shortcuts & gestures" onClose={() => setDialog(null)}>
          {[
            ["Keyboard", [["Esc", "Closes a menu, a dropdown list, or a dialog"]]],
            [
              "Mouse on a depth sheet",
              [
                ["Wheel", "Zooms vertically around the cursor"],
                ["Shift + wheel", "Pans the log sheet sideways"],
                ["Drag the ruler", "Stretches and compresses the scale"],
                ["Drag vertically", "Picks the depth interval to read"],
                ["Drag sideways", "Pans a log sheet wider than its panel"],
                ["Click", "Pins the reading at that depth; click again to release it"],
                ["Right click", "The marker menu, on the log sheet and the correlation sheet"],
              ],
            ],
          ].map(([group, rows]) => (
            <Panel key={group as string} title={group as string}>
              {(rows as string[][]).map(([key, description]) => (
                <div key={key} style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                  <span style={{ font: "500 11px var(--font-mono)", color: "var(--accent)", flex: "none" }}>
                    {key}
                  </span>
                  <span style={{ font: "400 11.5px var(--font-sans)", color: "var(--ink-3)", textAlign: "right" }}>
                    {description}
                  </span>
                </div>
              ))}
            </Panel>
          ))}
        </Modal>
      )}
      {dialog && !dialog.startsWith("curve-op:") && !dialog.startsWith("data:") &&
        !DIALOGS.includes(dialog) && (
        <Modal title="Not available yet" onClose={() => setDialog(null)}>
          <Notice tone="info">
            The “{dialog}” panel does not open from this menu. Its function lives on the related screen —
            parameters in the right rail of the Log screen, members on the project browser. Fields,
            wells, markers, zones and curve sets are in the Data menu.
          </Notice>
        </Modal>
      )}
    </>
  );
}

/**
 * Jendela yang benar-benar dibuka dari sini.
 *
 * Bukan hiasan: apa pun yang tidak ada di daftar ini mendapat jendela "Not
 * available yet" **di atas** jendelanya sendiri, sebab keduanya digambar dari
 * `dialog` yang sama. Sebuah jendela baru yang lupa didaftarkan karena itu
 * terbuka dengan benar dan langsung tertutupi oleh kalimat yang mengatakan ia
 * tidak ada.
 */
const DIALOGS = ["import-las", "import-image", "activity", "about", "shortcuts",
                 "log-colors"];

function ImportLasDialog({
  project,
  onClose,
  onImported,
}: {
  project: Project;
  onClose: () => void;
  onImported: (upload: LasUpload) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [wellName, setWellName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    const form = new FormData();
    form.append("project", String(project.id));
    form.append("file", file);
    if (wellName.trim()) form.append("well_name", wellName.trim());
    try {
      const result = await api.upload<LasUpload>("/api/las-uploads/", form);
      onImported(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Import a LAS file"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button variant="primary" busy={busy} disabled={!file} onClick={upload}>
            Import
          </Button>
        </>
      }
    >
      <label
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          const dropped = event.dataTransfer.files[0];
          if (dropped) setFile(dropped);
        }}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 9,
          padding: "30px 20px",
          border: `1.5px dashed ${dragOver ? "var(--accent)" : "var(--border-control)"}`,
          borderRadius: "var(--radius-panel)",
          background: dragOver ? "var(--accent-wash)" : "var(--panel-inset)",
          cursor: "pointer",
        }}
      >
        {Icon.upload({ size: 22, color: "var(--ink-5)" })}
        <span style={{ font: "500 12px var(--font-sans)", color: "var(--ink-2)" }}>
          {file ? file.name : "Drop a .las file here, or click to choose one"}
        </span>
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
          {file ? `${(file.size / 1024).toFixed(0)} KB` : "LAS 2.0"}
        </span>
        <input
          type="file"
          accept=".las,.LAS,text/plain"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          style={{ display: "none" }}
        />
      </label>

      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>
          Well name (leave blank to use the name in the file)
        </span>
        <input
          value={wellName}
          onChange={(event) => setWellName(event.target.value)}
          placeholder="mis. KTI-14 ST2"
          style={{
            height: 32,
            padding: "0 10px",
            background: "var(--panel-inset)",
            border: "1px solid var(--line-strong)",
            borderRadius: "var(--radius)",
            font: "400 12.5px var(--font-sans)",
            color: "var(--ink)",
            outline: "none",
          }}
        />
      </div>

      {error && <Notice tone="error">{error}</Notice>}
      <Notice tone="info">
        Importing into a well that already has curves publishes a new version — the old one stays
        stored so that earlier interpretations can be reproduced.
      </Notice>
    </Modal>
  );
}

function ActivityDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const activity = useAsync(
    (signal) => api.get<AuditEvent[]>(`/api/projects/${project.id}/activity/`, undefined, signal),
    [project.id],
  );

  return (
    <Modal title={`Riwayat — ${project.name}`} onClose={onClose} width={620}>
      {activity.loading && <Loading />}
      {activity.data?.length === 0 && <Empty>No activity has been recorded yet.</Empty>}
      {activity.data?.map((event) => (
        <div
          key={event.id}
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 10,
            padding: "7px 9px",
            borderBottom: "1px solid var(--line-soft)",
          }}
        >
          <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)", width: 118, flex: "none" }}>
            {new Date(event.created_at).toLocaleString("en-US")}
          </span>
          <span style={{ font: "500 11.5px var(--font-sans)", color: "var(--ink)", width: 130, flex: "none" }}>
            {event.actor_detail?.display_name ?? "Sistem"}
          </span>
          <span style={{ font: "400 11.5px var(--font-sans)", color: "var(--ink-3)" }}>
            {event.verb_label} {event.target_label}
          </span>
        </div>
      ))}
    </Modal>
  );
}

/**
 * Load an ASCII borehole-image export onto a well.
 *
 * The tool, the pad count and the bit size are asked for rather than guessed.
 * None of the three is recoverable from a table of numbers, and all three
 * change what the screen shows: the bit size scales every dip read off the
 * pass, and a pass loaded at 8½" when the hole was 12¼" is wrong by a third
 * everywhere, consistently enough to look right.
 */
function ImportImageDialog({
  well,
  onClose,
  onImported,
}: {
  well: Well;
  onClose: () => void;
  onImported: (name: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [tool, setTool] = useState("FMI");
  const [pads, setPads] = useState(8);
  const [holeInches, setHoleInches] = useState(8.5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    const form = new FormData();
    form.append("well", String(well.id));
    form.append("file", file);
    if (name.trim()) form.append("name", name.trim());
    form.append("tool", tool);
    form.append("pads", String(pads));
    form.append("hole_diameter", String(holeInches * 0.0254));
    try {
      const made = await api.upload<{ name: string }>("/api/image-logs/upload/", form);
      onImported(made.name);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Impor citra lubang bor"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Batal</Button>
          <Button variant="primary" busy={busy} disabled={!file} onClick={send}>Impor</Button>
        </>
      }
    >
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          padding: "26px 16px",
          border: "1.5px dashed var(--border-control)",
          borderRadius: "var(--radius-panel)",
          background: "var(--panel-inset)",
          cursor: "pointer",
        }}
      >
        {Icon.upload({ size: 20, color: "var(--ink-5)" })}
        <span style={{ font: "500 12px var(--font-sans)", color: "var(--ink-2)" }}>
          {file ? file.name : "Pilih berkas citra (ASCII)"}
        </span>
        <input
          type="file"
          accept=".txt,.csv,.asc,.dat,text/plain"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          style={{ display: "none" }}
        />
      </label>

      <TextField label="Nama lintasan" value={name} onChange={setName}
                 placeholder={file ? file.name : "mis. FMI 2050–2100 m"} />
      <Dropdown
        label="Alat"
        value={tool}
        options={[
          { value: "FMI", label: "FMI — mikroresistivitas, 8 pad" },
          { value: "FMS", label: "FMS — mikroresistivitas, 4 pad" },
          { value: "OBMI", label: "OBMI — lumpur berbasis minyak" },
          { value: "STAR", label: "STAR — mikroresistivitas" },
          { value: "UBI", label: "UBI — amplitudo ultrasonik" },
          { value: "CBIL", label: "CBIL — ultrasonik" },
        ]}
        onChange={(value) => {
          setTool(value);
          setPads(value === "FMS" ? 4 : 8);
        }}
      />
      <NumberField label="Jumlah pad" value={pads} onChange={setPads} step={1} min={1} max={16} />
      <NumberField label="Diameter lubang" unit="in" value={holeInches}
                   onChange={setHoleInches} step={0.125} min={3} max={26} />

      {error && <Notice tone="error">{error}</Notice>}
      <Notice tone="info">
        Bentuk yang dibaca adalah tabel ASCII: kolom pertama kedalaman, sisanya nilai tiap bin
        di sekeliling lubang. Baris judul dan baris komentar dilewati sendiri. DLIS dan LIS
        tidak dibaca — keduanya biner dan berlapis, dan pengurai setengah jadi yang salah
        membaca orientasinya lebih buruk daripada penolakan yang jujur.
      </Notice>
    </Modal>
  );
}
